import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, AlertTriangle, AlertCircle, Info, CreditCard, RefreshCw, Building2, Zap, Shield, Pencil, Save, X, Check, TrendingUp, DollarSign, Users, ArrowUpRight, ArrowDownRight, Loader2 } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import DailyBriefingBar from '../components/DailyBriefingBar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'pending_setup', label: 'Pending Setup' },
  { value: 'needs_migration', label: 'Needs Migration' },
  { value: 'mismatch', label: 'Mismatch' },
  { value: 'deprecated', label: 'Deprecated' },
  { value: 'dormant', label: 'Dormant' },
  { value: 'pending_verification', label: 'Pending Verification' }
];

const STATUS_CONFIG = {
  active: { bg: '#DCFCE7', text: '#166534', border: '#BBF7D0', label: 'Active' },
  pending_setup: { bg: '#DBEAFE', text: '#1E40AF', border: '#93C5FD', label: 'Pending Setup' },
  needs_migration: { bg: '#FEF3C7', text: '#92400E', border: '#FDE68A', label: 'Needs Migration' },
  mismatch: { bg: '#FEF2F2', text: '#991B1B', border: '#FECACA', label: 'MISMATCH' },
  deprecated: { bg: '#F3F4F6', text: '#6B7280', border: '#D1D5DB', label: 'Deprecated' },
  dormant: { bg: '#F3F4F6', text: '#6B7280', border: '#D1D5DB', label: 'Dormant' },
  pending_verification: { bg: '#FEF3C7', text: '#92400E', border: '#FDE68A', label: 'Verify' }
};

const ALERT_CONFIG = {
  critical: { icon: AlertTriangle, bg: '#FEF2F2', border: '#FECACA', text: '#991B1B', accent: '#DC2626' },
  warning: { icon: AlertCircle, bg: '#FFFBEB', border: '#FDE68A', text: '#92400E', accent: '#D97706' },
  info: { icon: Info, bg: '#EFF6FF', border: '#BFDBFE', text: '#1E40AF', accent: '#3B82F6' }
};

const PIE_COLORS = ['#D4AF37', '#8B7355', '#4A3A2E', '#13B5EA', '#635BFF', '#16A34A'];

const formatCurrency = (val, currency = 'gbp') => {
  const sym = currency === 'gbp' ? '£' : currency === 'usd' ? '$' : '€';
  return `${sym}${Number(val || 0).toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const formatMonth = (monthStr) => {
  if (!monthStr) return '';
  const [y, m] = monthStr.split('-');
  const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  return `${months[parseInt(m, 10) - 1]} ${y.slice(2)}`;
};

/* ─── Small reusable pieces ─── */

const StatusBadge = ({ status }) => {
  const config = STATUS_CONFIG[status] || STATUS_CONFIG.pending_setup;
  return (
    <span className="text-xs font-semibold px-2.5 py-1 rounded-full uppercase tracking-wider" style={{ backgroundColor: config.bg, color: config.text, border: `1px solid ${config.border}` }} data-testid={`status-badge-${status}`}>
      {config.label}
    </span>
  );
};

const EditableField = ({ label, value, onSave, mono, placeholder }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const handleSave = () => { onSave(draft); setEditing(false); };
  const handleCancel = () => { setDraft(value || ''); setEditing(false); };

  if (editing) {
    return (
      <div>
        <label className="text-xs uppercase tracking-widest" style={{ color: '#8B7355' }}>{label}</label>
        <div className="flex items-center gap-2 mt-1">
          <input type="text" value={draft} onChange={(e) => setDraft(e.target.value)}
            className={`flex-1 px-3 py-1.5 border rounded-sm text-sm focus:outline-none focus:ring-2 ${mono ? 'font-mono' : ''}`}
            style={{ borderColor: '#D4AF37' }} placeholder={placeholder} autoFocus
            data-testid={`edit-input-${label.toLowerCase().replace(/\s/g, '-')}`} />
          <button onClick={handleSave} className="p-1.5 rounded-sm" style={{ backgroundColor: '#D4AF37' }} data-testid={`save-${label.toLowerCase().replace(/\s/g, '-')}`}>
            <Check className="w-3.5 h-3.5 text-white" />
          </button>
          <button onClick={handleCancel} className="p-1.5 rounded-sm" style={{ backgroundColor: '#F3F4F6' }}>
            <X className="w-3.5 h-3.5" style={{ color: '#6B7280' }} />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group">
      <label className="text-xs uppercase tracking-widest" style={{ color: '#8B7355' }}>{label}</label>
      <div className="flex items-center gap-2 mt-0.5">
        <p className={`${mono ? 'font-mono text-xs' : 'font-medium text-sm'}`} style={{ color: value ? '#2C2416' : '#CBD5E1' }}>
          {value || placeholder || 'Not set'}
        </p>
        <button onClick={() => setEditing(true)} className="opacity-0 group-hover:opacity-100 transition-opacity p-1" data-testid={`edit-btn-${label.toLowerCase().replace(/\s/g, '-')}`}>
          <Pencil className="w-3 h-3" style={{ color: '#8B7355' }} />
        </button>
      </div>
    </div>
  );
};

const EditableNotes = ({ value, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value || '');
  const handleSave = () => { onSave(draft); setEditing(false); };

  if (editing) {
    return (
      <div>
        <textarea value={draft} onChange={(e) => setDraft(e.target.value)}
          className="w-full px-3 py-2 border rounded-sm text-xs leading-relaxed focus:outline-none focus:ring-2 resize-none"
          style={{ borderColor: '#D4AF37' }} rows={3} autoFocus data-testid="edit-notes-textarea" />
        <div className="flex justify-end gap-2 mt-1">
          <button onClick={() => { setDraft(value || ''); setEditing(false); }} className="text-xs px-2 py-1 rounded-sm" style={{ backgroundColor: '#F3F4F6', color: '#6B7280' }}>Cancel</button>
          <button onClick={handleSave} className="text-xs px-2 py-1 rounded-sm" style={{ backgroundColor: '#D4AF37', color: 'white' }} data-testid="save-notes-btn">Save</button>
        </div>
      </div>
    );
  }

  return (
    <div className="p-3 rounded-sm text-xs leading-relaxed cursor-pointer group transition-all duration-200 hover:border-amber-300"
      style={{ backgroundColor: '#FDFCFB', border: '1px solid #E5E1DB', color: '#4A4033' }}
      onClick={() => setEditing(true)} data-testid="editable-notes">
      {value || <span style={{ color: '#CBD5E1' }}>Click to add notes...</span>}
      <Pencil className="w-3 h-3 inline-block ml-2 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: '#8B7355' }} />
    </div>
  );
};

const StatusSelect = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const config = STATUS_CONFIG[value] || STATUS_CONFIG.pending_setup;
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)}
        className="flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full uppercase tracking-wider cursor-pointer transition-all duration-200 hover:shadow-sm"
        style={{ backgroundColor: config.bg, color: config.text, border: `1px solid ${config.border}` }}
        data-testid="status-select-trigger">
        {config.label}<Pencil className="w-2.5 h-2.5" />
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-white border rounded-sm shadow-lg z-50" style={{ borderColor: '#E5E1DB' }} data-testid="status-select-dropdown">
          {STATUS_OPTIONS.map((opt) => {
            const optConfig = STATUS_CONFIG[opt.value];
            return (
              <button key={opt.value} onClick={() => { onChange(opt.value); setOpen(false); }}
                className="w-full text-left px-3 py-2 text-xs flex items-center gap-2 transition-all duration-200 hover:bg-gray-50"
                style={{ borderBottom: '1px solid #F5F1EB' }} data-testid={`status-option-${opt.value}`}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: optConfig.text }} />
                {opt.label}
                {opt.value === value && <Check className="w-3 h-3 ml-auto" style={{ color: '#D4AF37' }} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* ─── Revenue Summary Card ─── */
const MetricCard = ({ icon: Icon, label, value, sub, color, testId }) => (
  <div className="p-5 rounded-sm bg-white border" style={{ borderColor: '#E5E1DB' }} data-testid={testId}>
    <div className="flex items-center gap-2 mb-3">
      <div className="w-8 h-8 rounded-full flex items-center justify-center" style={{ backgroundColor: color + '18' }}>
        <Icon className="w-4 h-4" style={{ color }} />
      </div>
      <span className="text-xs uppercase tracking-widest font-medium" style={{ color: '#8B7355' }}>{label}</span>
    </div>
    <p className="text-2xl font-light" style={{ color: '#2C2416' }}>{value}</p>
    {sub && <p className="text-xs mt-1" style={{ color: '#8B7355' }}>{sub}</p>}
  </div>
);

/* ─── Custom Tooltip for charts ─── */
const ChartTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white px-3 py-2 rounded shadow-md border text-xs" style={{ borderColor: '#E5E1DB' }}>
      <p className="font-medium mb-1" style={{ color: '#2C2416' }}>{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>{p.name}: {formatCurrency(p.value)}</p>
      ))}
    </div>
  );
};

const PieTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const d = payload[0];
  return (
    <div className="bg-white px-3 py-2 rounded shadow-md border text-xs" style={{ borderColor: '#E5E1DB' }}>
      <p className="font-medium" style={{ color: '#2C2416' }}>{d.name}</p>
      <p style={{ color: d.payload?.fill }}>{formatCurrency(d.value)}</p>
    </div>
  );
};

/* ═══════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════ */
const PaymentInfrastructure = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [revenueData, setRevenueData] = useState(null);
  const [revenueLoading, setRevenueLoading] = useState(true);
  const [revenueError, setRevenueError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchAccounts();
    fetchRevenue();
  }, []);

  const fetchAccounts = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/infrastructure/accounts`, { withCredentials: true });
      setAccounts(res.data);
    } catch (error) {
      console.error('Error fetching infrastructure data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchRevenue = async () => {
    setRevenueLoading(true);
    setRevenueError(null);
    try {
      const res = await axios.get(`${API_URL}/api/stripe/revenue-overview`, { withCredentials: true });
      setRevenueData(res.data);
    } catch (error) {
      console.error('Error fetching revenue:', error);
      setRevenueError('Unable to fetch Stripe data');
    } finally {
      setRevenueLoading(false);
    }
  };

  const saveAccount = async (accountId, updates) => {
    setSaving(accountId);
    try {
      const res = await axios.put(`${API_URL}/api/infrastructure/accounts/${accountId}`, updates, { withCredentials: true });
      setAccounts(prev => prev.map(a => a.id === accountId ? res.data : a));
    } catch (error) { console.error('Error saving:', error); }
    finally { setSaving(null); }
  };

  const resolveAlert = async (accountId, alertIndex) => {
    try {
      const res = await axios.put(`${API_URL}/api/infrastructure/accounts/${accountId}/resolve-alert/${alertIndex}`, {}, { withCredentials: true });
      setAccounts(prev => prev.map(a => a.id === accountId ? res.data : a));
    } catch (error) { console.error('Error resolving alert:', error); }
  };

  const updateStripeField = (account, field, value) => {
    const stripe = { ...account.stripe, [field]: value };
    if (field === 'status') stripe.last_verified = new Date().toISOString();
    saveAccount(account.id, { stripe });
  };

  const updateXeroField = (account, field, value) => {
    const xero = { ...account.xero, [field]: value };
    if (field === 'status') xero.last_verified = new Date().toISOString();
    saveAccount(account.id, { xero });
  };

  const updateAppStatus = (account, appIdx, newStatus) => {
    const apps = [...account.emergent_apps];
    apps[appIdx] = { ...apps[appIdx], stripe_key_status: newStatus };
    saveAccount(account.id, { emergent_apps: apps });
  };

  const totalAlerts = accounts.reduce((sum, a) => sum + (a.alerts || []).filter(al => !al.resolved).length, 0);
  const criticalAlerts = accounts.reduce((sum, a) => sum + (a.alerts || []).filter(al => !al.resolved && al.type === 'critical').length, 0);

  /* ─── Prepare chart data ─── */
  const monthlyChartData = (revenueData?.monthly_trend || []).map(d => ({
    month: formatMonth(d.month),
    Revenue: d.revenue,
  }));

  const companyPieData = (revenueData?.companies || [])
    .filter(c => c.connected && c.revenue > 0)
    .map(c => ({ name: c.company_name, value: c.revenue }));

  const revenueVsPayoutsPie = revenueData ? [
    { name: 'Revenue', value: revenueData.total_revenue },
    { name: 'Payouts', value: revenueData.total_payouts },
  ].filter(d => d.value > 0) : [];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAF9F7' }}>
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#D4AF37' }}></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAF9F7' }} data-testid="payment-infrastructure">
      <DailyBriefingBar />

      {criticalAlerts > 0 && (
        <div style={{ backgroundColor: '#DC2626', color: 'white' }} data-testid="critical-alert-banner">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 py-3">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-5 h-5 flex-shrink-0" />
              <p className="font-semibold text-sm">
                {criticalAlerts} CRITICAL ALERT{criticalAlerts > 1 ? 'S' : ''}: Payment routing misconfiguration detected. Immediate action required.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <header className="bg-white" style={{ borderBottom: '1px solid #E5E1DB' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <button onClick={() => navigate('/dashboard')} className="flex items-center gap-2 mb-4 transition-all duration-200" style={{ color: '#8B7355' }} data-testid="back-to-dashboard-button">
            <ArrowLeft className="w-5 h-5" /><span>Back to Dashboard</span>
          </button>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#2C2416' }}>
                <CreditCard className="w-6 h-6" style={{ color: '#D4AF37' }} />
              </div>
              <div>
                <h1 className="text-3xl font-light tracking-tight" style={{ color: '#2C2416' }}>Revenue & Payments</h1>
                <p className="text-sm mt-1" style={{ color: '#8B7355' }}>Stripe revenue tracking, account mapping & infrastructure</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {totalAlerts > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 rounded-sm" style={{ backgroundColor: criticalAlerts > 0 ? '#FEF2F2' : '#FFFBEB', border: `1px solid ${criticalAlerts > 0 ? '#FECACA' : '#FDE68A'}` }}>
                  <AlertTriangle className="w-4 h-4" style={{ color: criticalAlerts > 0 ? '#DC2626' : '#D97706' }} />
                  <span className="text-sm font-medium" style={{ color: criticalAlerts > 0 ? '#991B1B' : '#92400E' }}>{totalAlerts} alert{totalAlerts > 1 ? 's' : ''}</span>
                </div>
              )}
              <button onClick={fetchRevenue} className="flex items-center gap-2 px-4 py-2 rounded-sm text-sm transition-all hover:shadow-sm"
                style={{ backgroundColor: '#2C2416', color: '#D4AF37' }} data-testid="refresh-revenue-btn">
                <RefreshCw className={`w-4 h-4 ${revenueLoading ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="flex gap-1 mt-6 border-b" style={{ borderColor: '#E5E1DB' }}>
            {[
              { id: 'overview', label: 'Revenue Overview', icon: TrendingUp },
              { id: 'infrastructure', label: 'Account Infrastructure', icon: Building2 },
            ].map(tab => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)}
                className="flex items-center gap-2 px-4 py-2.5 text-sm font-medium transition-all relative"
                style={{
                  color: activeTab === tab.id ? '#D4AF37' : '#8B7355',
                  borderBottom: activeTab === tab.id ? '2px solid #D4AF37' : '2px solid transparent',
                  marginBottom: '-1px',
                }}
                data-testid={`tab-${tab.id}`}>
                <tab.icon className="w-4 h-4" />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 lg:px-8 py-8 space-y-8">

        {/* ═══ TAB: REVENUE OVERVIEW ═══ */}
        {activeTab === 'overview' && (
          <>
            {revenueLoading ? (
              <div className="flex items-center justify-center py-20">
                <Loader2 className="w-8 h-8 animate-spin" style={{ color: '#D4AF37' }} />
                <span className="ml-3 text-sm" style={{ color: '#8B7355' }}>Loading Stripe data...</span>
              </div>
            ) : revenueError ? (
              <div className="bg-white border rounded-sm p-8 text-center" style={{ borderColor: '#FECACA' }} data-testid="revenue-error">
                <AlertTriangle className="w-10 h-10 mx-auto mb-3" style={{ color: '#DC2626' }} />
                <p className="text-sm font-medium" style={{ color: '#991B1B' }}>{revenueError}</p>
                <button onClick={fetchRevenue} className="mt-4 text-xs px-4 py-2 rounded-sm" style={{ backgroundColor: '#2C2416', color: '#D4AF37' }}>Try Again</button>
              </div>
            ) : revenueData ? (
              <>
                {/* KPI Cards */}
                <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="revenue-kpi-grid">
                  <MetricCard icon={TrendingUp} label="Total Revenue" value={formatCurrency(revenueData.total_revenue)} sub="All connected accounts" color="#16A34A" testId="kpi-total-revenue" />
                  <MetricCard icon={ArrowDownRight} label="Total Payouts" value={formatCurrency(revenueData.total_payouts)} sub="Bank transfers" color="#635BFF" testId="kpi-total-payouts" />
                  <MetricCard icon={DollarSign} label="Available" value={formatCurrency(revenueData.total_available)} sub="Ready to withdraw" color="#D4AF37" testId="kpi-available" />
                  <MetricCard icon={DollarSign} label="Pending" value={formatCurrency(revenueData.total_pending)} sub="Processing" color="#D97706" testId="kpi-pending" />
                  <MetricCard icon={Users} label="Customers" value={revenueData.total_customers} sub="Across all accounts" color="#8B7355" testId="kpi-customers" />
                </div>

                {/* Charts Row */}
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Monthly Revenue Bar Chart */}
                  <div className="lg:col-span-2 bg-white border rounded-sm p-6" style={{ borderColor: '#E5E1DB' }} data-testid="monthly-revenue-chart">
                    <h3 className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: '#8B7355' }}>Monthly Revenue</h3>
                    {monthlyChartData.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <BarChart data={monthlyChartData} margin={{ top: 5, right: 10, left: 0, bottom: 5 }}>
                          <XAxis dataKey="month" tick={{ fontSize: 11, fill: '#8B7355' }} axisLine={{ stroke: '#E5E1DB' }} tickLine={false} />
                          <YAxis tick={{ fontSize: 11, fill: '#8B7355' }} axisLine={false} tickLine={false} tickFormatter={(v) => `£${v >= 1000 ? (v / 1000).toFixed(0) + 'k' : v}`} />
                          <Tooltip content={<ChartTooltip />} />
                          <Bar dataKey="Revenue" fill="#D4AF37" radius={[4, 4, 0, 0]} />
                        </BarChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-[280px]">
                        <p className="text-sm" style={{ color: '#8B7355' }}>No revenue data yet</p>
                      </div>
                    )}
                  </div>

                  {/* Revenue vs Payouts Donut */}
                  <div className="bg-white border rounded-sm p-6" style={{ borderColor: '#E5E1DB' }} data-testid="revenue-vs-payouts-chart">
                    <h3 className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: '#8B7355' }}>Revenue vs Payouts</h3>
                    {revenueVsPayoutsPie.length > 0 ? (
                      <ResponsiveContainer width="100%" height={280}>
                        <PieChart>
                          <Pie data={revenueVsPayoutsPie} cx="50%" cy="50%" innerRadius={60} outerRadius={90} paddingAngle={3} dataKey="value">
                            {revenueVsPayoutsPie.map((_, i) => (
                              <Cell key={i} fill={i === 0 ? '#D4AF37' : '#4A3A2E'} />
                            ))}
                          </Pie>
                          <Tooltip content={<PieTooltip />} />
                          <Legend iconType="circle" wrapperStyle={{ fontSize: 12, color: '#8B7355' }} />
                        </PieChart>
                      </ResponsiveContainer>
                    ) : (
                      <div className="flex items-center justify-center h-[280px]">
                        <p className="text-sm" style={{ color: '#8B7355' }}>No data</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Per-Company Revenue Cards */}
                {revenueData.companies && revenueData.companies.length > 0 && (
                  <div data-testid="company-revenue-section">
                    <h3 className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: '#8B7355' }}>Revenue by Company</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {revenueData.companies.map((co) => (
                        <div key={co.company_id} className="bg-white border rounded-sm p-5" style={{ borderColor: co.connected ? '#E5E1DB' : '#FECACA' }} data-testid={`company-revenue-${co.company_id}`}>
                          <div className="flex items-center justify-between mb-3">
                            <h4 className="text-sm font-medium" style={{ color: '#2C2416' }}>{co.company_name}</h4>
                            <span className={`text-xs px-2 py-0.5 rounded-full ${co.connected ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                              {co.connected ? 'Connected' : 'Not Connected'}
                            </span>
                          </div>
                          {co.connected ? (
                            <div className="space-y-2">
                              <div className="flex justify-between items-baseline">
                                <span className="text-xs" style={{ color: '#8B7355' }}>Revenue</span>
                                <span className="text-lg font-light" style={{ color: '#2C2416' }}>{formatCurrency(co.revenue, co.currency)}</span>
                              </div>
                              <div className="flex justify-between items-baseline">
                                <span className="text-xs" style={{ color: '#8B7355' }}>Available</span>
                                <span className="text-sm" style={{ color: '#16A34A' }}>{formatCurrency(co.balance_available, co.currency)}</span>
                              </div>
                              <div className="flex justify-between items-baseline">
                                <span className="text-xs" style={{ color: '#8B7355' }}>Customers</span>
                                <span className="text-sm" style={{ color: '#2C2416' }}>{co.customer_count}</span>
                              </div>
                              {/* Mini sparkline bar for this company */}
                              {co.monthly_trend && co.monthly_trend.length > 0 && (
                                <div className="pt-2 mt-2" style={{ borderTop: '1px solid #F5F1EB' }}>
                                  <ResponsiveContainer width="100%" height={50}>
                                    <BarChart data={co.monthly_trend.map(d => ({ m: formatMonth(d.month), v: d.revenue }))}>
                                      <Bar dataKey="v" fill="#D4AF37" radius={[2, 2, 0, 0]} />
                                    </BarChart>
                                  </ResponsiveContainer>
                                </div>
                              )}
                            </div>
                          ) : (
                            <p className="text-xs" style={{ color: '#991B1B' }}>{co.error || 'Stripe key not configured'}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Recent Transactions */}
                {revenueData.recent_charges && revenueData.recent_charges.length > 0 && (
                  <div className="bg-white border rounded-sm overflow-hidden" style={{ borderColor: '#E5E1DB' }} data-testid="recent-transactions">
                    <div className="px-6 py-4" style={{ borderBottom: '1px solid #E5E1DB' }}>
                      <h3 className="text-sm font-semibold uppercase tracking-widest" style={{ color: '#8B7355' }}>Recent Transactions</h3>
                    </div>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr style={{ backgroundColor: '#FDFCFB', borderBottom: '1px solid #E5E1DB' }}>
                            <th className="text-left px-6 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: '#8B7355' }}>Date</th>
                            <th className="text-left px-6 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: '#8B7355' }}>Company</th>
                            <th className="text-left px-6 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: '#8B7355' }}>Description</th>
                            <th className="text-right px-6 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: '#8B7355' }}>Amount</th>
                            <th className="text-center px-6 py-3 text-xs font-semibold uppercase tracking-wider" style={{ color: '#8B7355' }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {revenueData.recent_charges.map((ch, i) => (
                            <tr key={ch.id || i} style={{ borderBottom: '1px solid #F5F1EB' }} className="hover:bg-amber-50/20 transition-colors" data-testid={`transaction-row-${i}`}>
                              <td className="px-6 py-3 text-xs" style={{ color: '#4A4033' }}>
                                {ch.created ? new Date(ch.created * 1000).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: '2-digit' }) : '-'}
                              </td>
                              <td className="px-6 py-3 text-xs font-medium" style={{ color: '#2C2416' }}>{ch.company_name}</td>
                              <td className="px-6 py-3 text-xs" style={{ color: '#4A4033' }}>{ch.description || 'Payment'}</td>
                              <td className="px-6 py-3 text-sm text-right font-medium" style={{ color: '#2C2416' }}>{formatCurrency(ch.amount, ch.currency)}</td>
                              <td className="px-6 py-3 text-center">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${ch.paid ? 'bg-green-50 text-green-700' : ch.status === 'failed' ? 'bg-red-50 text-red-700' : 'bg-yellow-50 text-yellow-700'}`}>
                                  {ch.paid ? 'Paid' : ch.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    {revenueData.last_updated && (
                      <div className="px-6 py-2 text-right" style={{ backgroundColor: '#FAFAF9', borderTop: '1px solid #F5F1EB' }}>
                        <span className="text-xs" style={{ color: '#8B7355' }}>
                          Last synced: {new Date(revenueData.last_updated).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {/* Company Pie (if multiple companies) */}
                {companyPieData.length > 1 && (
                  <div className="bg-white border rounded-sm p-6" style={{ borderColor: '#E5E1DB' }} data-testid="revenue-by-company-pie">
                    <h3 className="text-sm font-semibold uppercase tracking-widest mb-4" style={{ color: '#8B7355' }}>Revenue Distribution</h3>
                    <ResponsiveContainer width="100%" height={250}>
                      <PieChart>
                        <Pie data={companyPieData} cx="50%" cy="50%" outerRadius={90} paddingAngle={3} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}>
                          {companyPieData.map((_, i) => (
                            <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip content={<PieTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </>
            ) : null}
          </>
        )}

        {/* ═══ TAB: INFRASTRUCTURE ═══ */}
        {activeTab === 'infrastructure' && (
          <>
            {/* Routing Overview */}
            <div className="bg-white border rounded-sm shadow-sm p-6" style={{ borderColor: '#E5E1DB' }} data-testid="routing-overview">
              <div className="flex items-center gap-2 mb-4">
                <RefreshCw className="w-5 h-5" style={{ color: '#D4AF37' }} />
                <h2 className="text-xl font-light" style={{ color: '#2C2416' }}>Payment Routing Overview</h2>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="text-center p-4 rounded-sm" style={{ backgroundColor: '#FDFCFB', border: '1px solid #E5E1DB' }}>
                  <CreditCard className="w-8 h-8 mx-auto mb-2" style={{ color: '#635BFF' }} />
                  <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#8B7355' }}>Stripe Accounts</p>
                  <p className="text-2xl font-light" style={{ color: '#2C2416' }}>{accounts.filter(a => a.stripe?.status !== 'dormant').length}</p>
                  <p className="text-xs" style={{ color: '#8B7355' }}>Active / In Setup</p>
                </div>
                <div className="text-center p-4 rounded-sm" style={{ backgroundColor: '#FDFCFB', border: '1px solid #E5E1DB' }}>
                  <Building2 className="w-8 h-8 mx-auto mb-2" style={{ color: '#13B5EA' }} />
                  <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#8B7355' }}>Xero Orgs</p>
                  <p className="text-2xl font-light" style={{ color: '#2C2416' }}>{accounts.filter(a => a.xero?.status !== 'dormant').length}</p>
                  <p className="text-xs" style={{ color: '#8B7355' }}>Active / In Setup</p>
                </div>
                <div className="text-center p-4 rounded-sm" style={{ backgroundColor: '#FDFCFB', border: '1px solid #E5E1DB' }}>
                  <Zap className="w-8 h-8 mx-auto mb-2" style={{ color: '#D4AF37' }} />
                  <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#8B7355' }}>Emergent Apps</p>
                  <p className="text-2xl font-light" style={{ color: '#2C2416' }}>{accounts.reduce((sum, a) => sum + (a.emergent_apps || []).length, 0)}</p>
                  <p className="text-xs" style={{ color: '#8B7355' }}>Connected</p>
                </div>
              </div>
            </div>

            {/* Company Infrastructure Cards */}
            {accounts.map((account) => {
              const activeAlerts = (account.alerts || []).filter(a => !a.resolved);
              const hasCritical = activeAlerts.some(a => a.type === 'critical');
              const isSaving = saving === account.id;

              return (
                <div key={account.id} className="bg-white border rounded-sm shadow-sm overflow-hidden relative" style={{ borderColor: hasCritical ? '#FECACA' : '#E5E1DB', borderLeftWidth: '4px', borderLeftColor: account.company_color }} data-testid={`infra-card-${account.company_name.toLowerCase().replace(/\s/g, '-')}`}>
                  {isSaving && (
                    <div className="absolute top-3 right-3 flex items-center gap-1.5 px-2 py-1 rounded-full text-xs" style={{ backgroundColor: '#D4AF37', color: 'white' }}>
                      <Save className="w-3 h-3 animate-pulse" /> Saving...
                    </div>
                  )}

                  {/* Company Header */}
                  <div className="px-6 py-5 flex items-center justify-between" style={{ borderBottom: '1px solid #E5E1DB' }}>
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: account.company_color + '15' }}>
                        <Building2 className="w-5 h-5" style={{ color: account.company_color }} />
                      </div>
                      <div>
                        <h3 className="text-lg font-medium" style={{ color: '#2C2416' }}>{account.company_name}</h3>
                        <p className="text-xs" style={{ color: '#8B7355' }}>Covers: {account.divisions_covered.join(' / ')}</p>
                      </div>
                    </div>
                    {activeAlerts.length > 0 && (
                      <span className="flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full" style={{ backgroundColor: hasCritical ? '#FEF2F2' : '#FFFBEB', color: hasCritical ? '#DC2626' : '#D97706' }}>
                        <AlertTriangle className="w-3.5 h-3.5" />
                        {activeAlerts.length} alert{activeAlerts.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>

                  {/* Alerts */}
                  {activeAlerts.length > 0 && (
                    <div className="px-6 py-3 space-y-2" style={{ backgroundColor: '#FAFAF9' }}>
                      {activeAlerts.map((alert, idx) => {
                        const alertIdx = (account.alerts || []).indexOf(alert);
                        const config = ALERT_CONFIG[alert.type] || ALERT_CONFIG.info;
                        const Icon = config.icon;
                        return (
                          <div key={idx} className="flex items-start gap-3 p-3 rounded-sm" style={{ backgroundColor: config.bg, border: `1px solid ${config.border}` }} data-testid={`alert-${account.id}-${alertIdx}`}>
                            <Icon className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: config.accent }} />
                            <p className="flex-1 text-sm" style={{ color: config.text }}>{alert.message}</p>
                            <button onClick={() => resolveAlert(account.id, alertIdx)} className="text-xs font-medium px-2 py-1 rounded-sm transition-all duration-200 hover:opacity-80 flex-shrink-0" style={{ backgroundColor: 'white', color: config.text, border: `1px solid ${config.border}` }} data-testid={`resolve-alert-${account.id}-${alertIdx}`}>
                              Resolve
                            </button>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Editable Account Details */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
                    {/* Stripe */}
                    <div className="px-6 py-5" style={{ borderRight: '1px solid #E5E1DB' }}>
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <CreditCard className="w-5 h-5" style={{ color: '#635BFF' }} />
                          <h4 className="text-sm font-semibold uppercase tracking-widest" style={{ color: '#635BFF' }}>Stripe</h4>
                        </div>
                        <StatusSelect value={account.stripe?.status} onChange={(v) => updateStripeField(account, 'status', v)} />
                      </div>
                      <div className="space-y-3">
                        <EditableField label="Account Name" value={account.stripe?.account_name} placeholder="e.g. Star Talent Group Ltd" onSave={(v) => updateStripeField(account, 'account_name', v)} />
                        <EditableField label="Account ID" value={account.stripe?.account_id} placeholder="acct_..." mono onSave={(v) => updateStripeField(account, 'account_id', v)} />
                        <EditableNotes value={account.stripe?.notes} onSave={(v) => updateStripeField(account, 'notes', v)} />
                        {account.stripe?.last_verified && (
                          <p className="text-xs" style={{ color: '#16A34A' }}>
                            Verified: {new Date(account.stripe.last_verified).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Xero */}
                    <div className="px-6 py-5">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center gap-2">
                          <Shield className="w-5 h-5" style={{ color: '#13B5EA' }} />
                          <h4 className="text-sm font-semibold uppercase tracking-widest" style={{ color: '#13B5EA' }}>Xero</h4>
                        </div>
                        <StatusSelect value={account.xero?.status} onChange={(v) => updateXeroField(account, 'status', v)} />
                      </div>
                      <div className="space-y-3">
                        <EditableField label="Organisation" value={account.xero?.organisation_name} placeholder="e.g. Star Talent Group Ltd" onSave={(v) => updateXeroField(account, 'organisation_name', v)} />
                        <EditableField label="Organisation ID" value={account.xero?.organisation_id} placeholder="org-..." mono onSave={(v) => updateXeroField(account, 'organisation_id', v)} />
                        <EditableNotes value={account.xero?.notes} onSave={(v) => updateXeroField(account, 'notes', v)} />
                        {account.xero?.last_verified && (
                          <p className="text-xs" style={{ color: '#16A34A' }}>
                            Verified: {new Date(account.xero.last_verified).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                          </p>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Emergent Apps */}
                  {account.emergent_apps && account.emergent_apps.length > 0 && (
                    <div className="px-6 py-4" style={{ backgroundColor: '#FDFCFB', borderTop: '1px solid #E5E1DB' }}>
                      <div className="flex items-center gap-2 mb-3">
                        <Zap className="w-4 h-4" style={{ color: '#D4AF37' }} />
                        <h4 className="text-xs font-semibold uppercase tracking-widest" style={{ color: '#8B7355' }}>Emergent Apps</h4>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {account.emergent_apps.map((app, idx) => (
                          <div key={idx} className="flex items-center justify-between p-3 rounded-sm bg-white border" style={{ borderColor: '#E5E1DB' }} data-testid={`emergent-app-${app.app_name.toLowerCase().replace(/\s/g, '-')}`}>
                            <div className="flex items-center gap-2">
                              <div className="w-2 h-2 rounded-full" style={{ backgroundColor: app.stripe_key_status === 'active' ? '#16A34A' : app.stripe_key_status === 'mismatch' ? '#DC2626' : '#D97706' }} />
                              <span className="text-sm font-medium" style={{ color: '#2C2416' }}>{app.app_name}</span>
                            </div>
                            <StatusSelect value={app.stripe_key_status} onChange={(v) => updateAppStatus(account, idx, v)} />
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Last Updated */}
                  <div className="px-6 py-2 text-right" style={{ backgroundColor: '#FAFAF9', borderTop: '1px solid #F5F1EB' }}>
                    <span className="text-xs" style={{ color: '#8B7355' }}>
                      Last updated: {account.updated_at ? new Date(account.updated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'Never'}
                    </span>
                  </div>
                </div>
              );
            })}
          </>
        )}
      </main>
    </div>
  );
};

export default PaymentInfrastructure;
